<template>
  <div :class="showInitial ? 'user-avatar-empty' : 'user-avatar'" :style="sizeStyle">
    <template v-if="showInitial">
      <span class="user-avatar-initial" :style="initialStyle">{{ userInitial }}</span>
    </template>
    <img v-else :src="imageUrl" alt="user profile image" @error="handleImageError" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useUserImgsStore } from '~/composables/useUserImgsStore';

interface Props {
  userInfo?: any;
  userId?: string;
  userName?: string;
  size?: number;
  color?: string;
  skipMissingImageFetch?: boolean;
}

const imageError = ref(false);
const props = withDefaults(defineProps<Props>(), {
  size: 32,
});
const userImgsStore = useUserImgsStore();
const userId = computed(() => props.userId || props.userInfo?.id);
const imageUrl = computed(() => {
  if (!userId.value) {
    return '/images/icon-gnb-my.svg';
  }
  return userImgsStore.userImages[userId.value] || '';
});

// userId 있을 때: URL 없음(로딩/미등록) 또는 로드 실패 → 이니셜 표시
const showInitial = computed(() => {
  if (!userId.value) {
    return false;
  }
  if (imageError.value) {
    return true;
  }
  return !imageUrl.value;
});

// 다른 사용자로 전환 시 이전 @error 상태가 남으면 프로필이 안 보임 → userId 변경마다 재시도
watch(
  () => userId.value,
  async id => {
    imageError.value = false;
    if (!id) return;
    // 조직도는 전체 이미지 목록을 공유한다. 행마다 개별 API를 호출하지 않는다.
    if (props.skipMissingImageFetch) return;
    // 값이 아니라 키 존재로 판단한다 — 이미지가 없는 사용자('')를 매번 다시 조회하지 않도록.
    if (!(id in userImgsStore.userImages)) {
      await userImgsStore.fetchUserImage(id);
    }
  },
  { immediate: true },
);

// 캐시/API 로드 후 URL이 채워지면 img 재시도
watch(imageUrl, url => {
  if (url && userId.value) {
    imageError.value = false;
  }
});

// 이름 첫 글자 추출
const userInitial = computed(() => {
  const name = props.userName || props.userInfo?.name || props.userInfo?.empNm || '';
  return name.charAt(0) || '?';
});

// 크기 스타일
const sizeStyle = computed(() => {
  const s: Record<string, string> = {};
  if (props.size !== 32) {
    s.width = `${props.size}px`;
    s.height = `${props.size}px`;
  }
  if (props.color) {
    s.background = props.color;
    s.boxShadow = 'none';
  }
  return s;
});

// 이니셜 폰트 크기
const initialStyle = computed(() => {
  if (props.size !== 32) {
    return { fontSize: `${Math.round(props.size * 0.45)}px` };
  }
  return {};
});

const handleImageError = () => {
  imageError.value = true;
};
</script>

<style scoped lang="scss">
.user-avatar-empty {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #424abd 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .user-avatar-initial {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
  }
}
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background: #898989;
  border: 1px solid #cdd1d5;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
